import { RequireAccess } from '@/components'
import { SettingsHeading, SettingsNav } from '@/ui'
import { EarlyAccessBadge } from '@/ui/constructs/gate'
import { ConfirmationDialog } from '@/ui/rapid-ui'
import { AlertCircleIcon, CheckCircleIcon, Loader2Icon, MessageCircleIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { FirstContactAutomationPanel } from './first-contact-automation-panel'
import { ProvisioningFailedCard } from './provisioning-failed-card'
import { ProvisioningProgressCard } from './provisioning-progress-card'
import { VerificationStepCard } from './verification-step-card'
import { WhatsAppActiveSection } from './whatsapp-active-section'
import { WhatsAppBusinessProfileForm } from './whatsapp-business-profile-form'
import { ConnectWhatsAppPanel, FailedSignupPanel } from './whatsapp-connect-panel'
import { useWhatsAppSettingsViewModel } from './whatsapp-settings.vm'

const NEW_INTEGRATIONS_DISABLED = false

export function WhatsAppSettings() {
	const { t } = useTranslation('common')
	const vm = useWhatsAppSettingsViewModel()

	return (
		<SettingsNav active="whatsapp" fullWidth={false}>
			<section className="space-y-6 pr-8 max-w-[800px]">
				<div className="flex items-center gap-2">
					<SettingsHeading>{t('whatsappSettings.title')}</SettingsHeading>
					{vm.whatsappEnabled && <EarlyAccessBadge />}
				</div>

				{vm.isLoadingStatus
					? (
						<div className="flex items-center justify-center py-12">
							<Loader2Icon className="size-6 animate-spin text-gray-400" />
						</div>
					)
					: !vm.whatsappEnabled
					? (
						<div className="flex flex-col items-center justify-center py-16 px-6 text-center">
							<div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-50 text-brand-600 mb-4">
								<MessageCircleIcon className="size-6" />
							</div>
							<p className="text-sm text-grey-500 max-w-md">{t('whatsappSettings.notAvailable')}</p>
						</div>
					)
					: (
						<RequireAccess
							permission="billing:manage"
							fallback={<p className="text-sm text-gray-500">{t('whatsappSettings.noPermission')}</p>}
						>
							<AnimatePresence>
								{vm.success && (
									<motion.div
										role="status"
										aria-live="polite"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2"
									>
										<CheckCircleIcon className="size-4" />
										{vm.success}
									</motion.div>
								)}
								{vm.error && (
									<motion.div
										role="alert"
										aria-live="assertive"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2"
									>
										<AlertCircleIcon className="size-4" />
										{vm.error}
									</motion.div>
								)}
							</AnimatePresence>

							{vm.isProvisioning && (
								<ProvisioningProgressCard
									provisioningStatus={vm.provisioningStatus}
								/>
							)}

							{vm.isAwaitingVerification && (
								<VerificationStepCard
									verificationRequestFailed={vm.verificationRequestFailed}
									resendCooldownRemaining={vm.resendCooldownRemaining}
									verificationAutoAttempts={vm.verificationAutoAttempts}
									verificationOnHold={vm.verificationOnHold}
								/>
							)}

							{vm.showProvisioningFailed && (
								<ProvisioningFailedCard
									provisioningError={vm.provisioningError}
									onRetry={vm.handleRetryProvisioning}
									isRetrying={vm.isRetrying}
									onDisconnect={() => void vm.handleDisconnect()}
									isDisconnecting={vm.isDisconnecting}
									retryUnavailable={NEW_INTEGRATIONS_DISABLED}
								/>
							)}

							{vm.showConnectPanel && (
								<ConnectWhatsAppPanel
									onActivate={vm.handleActivateAccount}
									isLoading={vm.isLoading}
									isActivatingAccount={vm.isActivatingAccount}
									unavailable={NEW_INTEGRATIONS_DISABLED}
									deactivatedDate={vm.wasDeactivatedForInactivity ? vm.deactivatedDate : null}
								/>
							)}

							{vm.showFailedSignup && (
								<FailedSignupPanel
									onReset={vm.handleResetSignup}
									isResetting={vm.isResetting}
								/>
							)}

							{vm.showActiveSection && (
								<WhatsAppActiveSection
									wabaDisplayName={vm.wabaDisplayName}
									scheduledDeactivationDate={vm.showInactivityWarning
										? vm.scheduledDeactivationDate
										: null}
									templates={vm.templates}
									isCreatingTemplate={vm.isCreatingTemplate}
									isSyncing={vm.isSyncing}
									isCreating={vm.isCreating}
									isLoading={vm.isLoading}
									isDisconnecting={vm.isDisconnecting}
									isUpdatingParameterMap={vm.isUpdatingParameterMap}
									showArchived={vm.showArchived}
									isEditing={vm.isEditing}
									onDisconnect={() => void vm.handleDisconnect()}
									onSyncTemplates={() => void vm.handleSyncTemplates()}
									onCreateTemplate={vm.handleCreateTemplate}
									onSetCreatingTemplate={vm.setIsCreatingTemplate}
									onUpdateParameterMap={vm.handleUpdateParameterMap}
									onSetShowArchived={vm.setShowArchived}
									onEditTemplate={vm.handleEditTemplate}
									onArchiveTemplate={vm.handleArchiveTemplate}
									onUnarchiveTemplate={vm.handleUnarchiveTemplate}
								/>
							)}

							{vm.showActiveSection && (
								<FirstContactAutomationPanel
									organisationId={vm.organisationId}
									templates={vm.activeTemplates}
									enabled={vm.firstContactAutomationEnabled}
									templateId={vm.firstContactTemplateId}
									scoreThreshold={vm.firstContactScoreThreshold ?? null}
									onSaved={() => void vm.refreshStatus()}
								/>
							)}

							{vm.showBusinessProfile && (
								<WhatsAppBusinessProfileForm wabaDisplayName={vm.wabaDisplayName} />
							)}
						</RequireAccess>
					)}

				<ConfirmationDialog ref={vm.confirmDialogRef} />
			</section>
		</SettingsNav>
	)
}
